import { useCallback, useMemo, type ReactNode } from "react";

import { useDynamicFormatString } from "@skilitics/localization";

import { TemplateImportError } from "../../errors/TemplateImportError.js";
import { useGetManifest } from "../../state";
import type { DesignerNode, DesignerNodeHandle, DesignerNodeViewProps, Obj } from "../../types/index.js";
import { LayoutContentContext } from "./LayoutContentContext.js";

export type LayoutableViewProps<TConfig extends Obj = Obj, TChildName extends string = string> = Omit<
  DesignerNodeViewProps<TConfig, TChildName>,
  "childNodes" | "onAddChild" | "onRemoveChild"
> & {
  children: ReactNode;
  items: DesignerNodeHandle[];
  onAddItem: (
    options: Pick<DesignerNode, "typeId" | "role"> & { definitionId?: string },
  ) => ReturnType<LayoutContentContext["onAddItem"]>;
  onRemoveItem: DesignerNodeViewProps<TConfig, TChildName>["onRemoveChild"];
  layoutableId?: number;
} & Pick<
    LayoutContentContext,
    "canAddItem" | "canBindSlotRef" | "layoutRootId" | "onImportItem" | "onImportSlotRef" | "nodeSelectionCriteria"
  > &
  Partial<Pick<LayoutContentContext, "canRemoveItem">>;

export const LayoutableView = ({
  id,
  typeId,
  role,
  onAddItem,
  onRemoveItem,
  canRemoveItem,
  onImportItem,
  onImportSlotRef,
  items,
  children,
  canAddItem,
  canBindSlotRef,
  layoutRootId,
  nodeSelectionCriteria,
  layoutableId,
}: LayoutableViewProps) => {
  const getManifest = useGetManifest();
  const format = useDynamicFormatString();

  const handleAddItem: LayoutContentContext["onAddItem"] = useCallback(
    ({ typeId: newTypeId, definitionId }) => {
      const {
        terminus: { roles },
      } = getManifest(newTypeId);
      if (!roles || roles.length === 0) throw new Error(`Cannot add item of type ${newTypeId} to ${typeId}:${id}.`);
      return onAddItem({ typeId: newTypeId, role: roles[0], definitionId });
    },
    [getManifest, id, onAddItem, typeId],
  );

  const handleCanRemoveItem: LayoutContentContext["canRemoveItem"] = useCallback(
    (removeId) => (canRemoveItem ? canRemoveItem(removeId) : true),
    [canRemoveItem],
  );

  const handleRemoveItem: LayoutContentContext["onRemoveItem"] = useCallback(
    (itemId) => {
      if (!items.find((item) => item.id === itemId)) return; // not found; do nothing

      if (!handleCanRemoveItem(itemId)) throw new Error(`Item with id ${itemId} on ${typeId}:${id} cannot be removed.`);
      onRemoveItem(itemId);
    },
    [handleCanRemoveItem, id, items, onRemoveItem, typeId],
  );

  const handleImportItem: LayoutContentContext["onImportItem"] = useCallback(
    (candidate) => {
      const can = canAddItem(candidate);
      if (!can.value) throw new TemplateImportError(format(can.reason));
      return onImportItem(candidate);
    },
    [canAddItem, format, onImportItem],
  );

  const context: LayoutContentContext = useMemo(
    () => ({
      items,
      onAddItem: handleAddItem,
      onRemoveItem: handleRemoveItem,
      onImportItem: handleImportItem,
      onImportSlotRef,
      canRemoveItem: handleCanRemoveItem,
      canAddItem,
      canBindSlotRef,
      layoutRootId,
      snapshotId: id,
      layoutableId: layoutableId ?? id,
      role,
      nodeSelectionCriteria,
    }),
    [
      canAddItem,
      canBindSlotRef,
      handleAddItem,
      handleCanRemoveItem,
      handleImportItem,
      handleRemoveItem,
      id,
      onImportSlotRef,
      items,
      layoutableId,
      layoutRootId,
      nodeSelectionCriteria,
      role,
    ],
  );

  // we only need to render the layout child because the items are pulled in
  // on the slots within that layout subtree
  return <LayoutContentContext.Provider value={context}>{children}</LayoutContentContext.Provider>;
};
